import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const PageContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #000000 0%, #333333 100%);
  color: white;
  padding: 8rem 0 6rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="2" fill="%23FFD700" opacity="0.1"/><circle cx="20" cy="30" r="1.5" fill="%23FFD700" opacity="0.08"/><circle cx="80" cy="70" r="1.5" fill="%23FFD700" opacity="0.08"/></svg>');
  }
  
  > * {
    position: relative;
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const HeroTitle = styled(motion.h1)`
  font-size: 4rem;
  margin-bottom: 1.5rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: -2px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
    letter-spacing: -1px;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.5rem;
  margin-bottom: 3rem;
  opacity: 0.9;
  font-weight: 300;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const HeroButtons = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const PrimaryButton = styled(Link)`
  background: #FFD700;
  color: #000000;
  padding: 1.2rem 2.5rem;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 700;
  font-size: 1.1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 15px rgba(255, 215, 0, 0.3);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(255, 215, 0, 0.4);
    background: #FFED4E;
  }
`;

const SecondaryButton = styled(Link)`
  background: transparent;
  color: white;
  padding: 1.2rem 2.5rem;
  border: 3px solid #FFD700;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 700;
  font-size: 1.1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: #FFD700;
    color: #000000;
    transform: translateY(-3px);
  }
`;

const FeaturedDealSection = styled.section`
  background: #FFD700;
  padding: 6rem 0;
  text-align: center;
`;

const FeaturedContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const DealBadge = styled.div`
  background: #000000;
  color: #FFD700;
  padding: 0.5rem 1.5rem;
  border-radius: 25px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: inline-block;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const DealTitle = styled.h2`
  font-size: 3.5rem;
  color: #000000;
  margin-bottom: 1rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: -2px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const DealSubtitle = styled.p`
  font-size: 1.3rem;
  color: #000000;
  margin-bottom: 2rem;
  font-weight: 400;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const DealDescription = styled.p`
  font-size: 1.1rem;
  color: #333333;
  margin-bottom: 2.5rem;
  line-height: 1.6;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
`;

const DealButton = styled(Link)`
  background: #000000;
  color: #FFD700;
  padding: 1.2rem 3rem;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 700;
  font-size: 1.1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
    background: #333333;
  }
`;

const MenuSection = styled.section`
  background: white;
  padding: 6rem 0;
`;

const MenuContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  text-align: center;
`;

const SectionTitle = styled.h2`
  font-size: 3rem;
  color: #000000;
  margin-bottom: 1rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: -2px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: #666666;
  margin-bottom: 3rem;
  font-weight: 400;
`;

const MenuGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const MenuCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border: 2px solid transparent;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    border-color: #FFD700;
  }
`;

const MenuCardImage = styled.div`
  height: 200px;
  background: linear-gradient(135deg, #000000 0%, #333333 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  color: #FFD700;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="1" fill="%23FFD700" opacity="0.2"/></svg>');
  }
`;

const MenuCardContent = styled.div`
  padding: 2rem;
  text-align: center;
`;

const MenuCardTitle = styled.h3`
  font-size: 1.5rem;
  color: #000000;
  margin-bottom: 1rem;
  font-weight: 700;
`;

const MenuCardDescription = styled.p`
  color: #666666;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const MenuCardButton = styled(Link)`
  background: #FFD700;
  color: #000000;
  padding: 0.8rem 1.5rem;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: #FFED4E;
    transform: translateY(-2px);
  }
`;

const AppSection = styled.section`
  background: #f8f9fa;
  padding: 6rem 0;
`;

const AppContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const AppInfo = styled.div`
  h2 {
    font-size: 3rem;
    color: #000000;
    margin-bottom: 1rem;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: -2px;
    
    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }
  
  p {
    font-size: 1.2rem;
    color: #666666;
    margin-bottom: 2rem;
    line-height: 1.6;
  }
`;

const AppFeatures = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const AppFeature = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  .icon {
    background: #FFD700;
    color: #000000;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
  }
  
  .text {
    h4 {
      color: #000000;
      margin-bottom: 0.25rem;
      font-weight: 700;
    }
    
    p {
      color: #666666;
      font-size: 0.9rem;
      margin: 0;
    }
  }
`;

const AppImage = styled.div`
  background: linear-gradient(135deg, #000000 0%, #333333 100%);
  height: 400px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #FFD700;
  font-size: 6rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="2" fill="%23FFD700" opacity="0.1"/><circle cx="20" cy="30" r="1.5" fill="%23FFD700" opacity="0.08"/><circle cx="80" cy="70" r="1.5" fill="%23FFD700" opacity="0.08"/></svg>');
  }
`;

const DeliverySection = styled.section`
  background: white;
  padding: 6rem 0;
`;

const DeliveryContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  text-align: center;
`;

const DeliveryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const DeliveryCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border: 2px solid transparent;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
    border-color: #FFD700;
  }
  
  .icon {
    background: #FFD700;
    color: #000000;
    width: 80px;
    height: 80px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    margin: 0 auto 1.5rem;
  }
  
  h3 {
    color: #000000;
    margin-bottom: 1rem;
    font-weight: 700;
    font-size: 1.3rem;
  }
  
  p {
    color: #666666;
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }
`;

const DeliveryButton = styled(Link)`
  background: #000000;
  color: #FFD700;
  padding: 0.8rem 1.5rem;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: #333333;
    transform: translateY(-2px);
  }
`;

const HomePage: React.FC = () => {
  return (
    <PageContainer>
      {/* Hero Section */}
      <HeroSection>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            Poule & Poulette
          </HeroTitle>
          <HeroSubtitle
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            De beste kipgerechten van België
          </HeroSubtitle>
          <HeroButtons
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
          >
            <PrimaryButton to="/reserveren">
              Reserveren <ArrowRight size={20} />
            </PrimaryButton>
            <SecondaryButton to="/takeaway">
              Takeaway <ArrowRight size={20} />
            </SecondaryButton>
          </HeroButtons>
        </HeroContent>
      </HeroSection>

      {/* Featured Deal Section */}
      <FeaturedDealSection>
        <FeaturedContent>
          <DealBadge>Nieuwe Deal</DealBadge>
          <DealTitle>Kip & Friet Combo</DealTitle>
          <DealSubtitle>
            Geniet van onze beroemde kip met verse friet voor slechts €12,99
          </DealSubtitle>
          <DealDescription>
            Onze signature kipgerechten met knapperige friet, verse salade en een drankje naar keuze. 
            Perfect voor lunch of diner. Beschikbaar in alle vestigingen.
          </DealDescription>
          <DealButton to="/menu">
            Bestel Nu <ArrowRight size={20} />
          </DealButton>
        </FeaturedContent>
      </FeaturedDealSection>

      {/* Menu Section */}
      <MenuSection>
        <MenuContent>
          <SectionTitle>Onze Menu's</SectionTitle>
          <SectionSubtitle>
            Ontdek onze heerlijke gerechten, van klassieke kip tot moderne creaties
          </SectionSubtitle>
          
          <MenuGrid>
            <MenuCard
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <MenuCardImage>🍗</MenuCardImage>
              <MenuCardContent>
                <MenuCardTitle>Kipgerechten</MenuCardTitle>
                <MenuCardDescription>
                  Onze beroemde kipbereidingen, van gegrilde tot gefrituurde varianten
                </MenuCardDescription>
                <MenuCardButton to="/menu">
                  Bekijk Menu <ArrowRight size={16} />
                </MenuCardButton>
              </MenuCardContent>
            </MenuCard>

            <MenuCard
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <MenuCardImage>🥗</MenuCardImage>
              <MenuCardContent>
                <MenuCardTitle>Salades & Sides</MenuCardTitle>
                <MenuCardDescription>
                  Verse salades en heerlijke bijgerechten om je maaltijd compleet te maken
                </MenuCardDescription>
                <MenuCardButton to="/menu">
                  Bekijk Menu <ArrowRight size={16} />
                </MenuCardButton>
              </MenuCardContent>
            </MenuCard>

            <MenuCard
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <MenuCardImage>🍹</MenuCardImage>
              <MenuCardContent>
                <MenuCardTitle>Dranken & Desserts</MenuCardTitle>
                <MenuCardDescription>
                  Verfrissende dranken en zoete desserts om je maaltijd af te sluiten
                </MenuCardDescription>
                <MenuCardButton to="/menu">
                  Bekijk Menu <ArrowRight size={16} />
                </MenuCardButton>
              </MenuCardContent>
            </MenuCard>
          </MenuGrid>
        </MenuContent>
      </MenuSection>

      {/* App Section */}
      <AppSection>
        <AppContent>
          <AppInfo>
            <h2>Download Onze App</h2>
            <p>
              Bestel eenvoudig online, ontvang exclusieve aanbiedingen en skip de wachtrij. 
              Download nu de Poule & Poulette app!
            </p>
            
            <AppFeatures>
              <AppFeature>
                <div className="icon">⚡</div>
                <div className="text">
                  <h4>Snelle Bestelling</h4>
                  <p>Bestel in minder dan 30 seconden</p>
                </div>
              </AppFeature>
              
              <AppFeature>
                <div className="icon">🎁</div>
                <div className="text">
                  <h4>Exclusieve Deals</h4>
                  <p>Speciale aanbiedingen alleen in de app</p>
                </div>
              </AppFeature>
              
              <AppFeature>
                <div className="icon">📱</div>
                <div className="text">
                  <h4>Loyalty Programma</h4>
                  <p>Verdien punten bij elke bestelling</p>
                </div>
              </AppFeature>
            </AppFeatures>
            
            <PrimaryButton to="/takeaway">
              Download App <ArrowRight size={20} />
            </PrimaryButton>
          </AppInfo>
          
          <AppImage>
            📱
          </AppImage>
        </AppContent>
      </AppSection>

      {/* Delivery Section */}
      <DeliverySection>
        <DeliveryContent>
          <SectionTitle>Levering & Takeaway</SectionTitle>
          <SectionSubtitle>
            Geniet van Poule & Poulette waar en wanneer je maar wilt
          </SectionSubtitle>
          
          <DeliveryGrid>
            <DeliveryCard
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="icon">🚚</div>
              <h3>Thuislevering</h3>
              <p>
                Laat je favoriete gerechten thuis bezorgen door onze partners
              </p>
              <DeliveryButton to="/takeaway">
                Bestel Nu <ArrowRight size={16} />
              </DeliveryButton>
            </DeliveryCard>

            <DeliveryCard
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="icon">🏪</div>
              <h3>Takeaway</h3>
              <p>
                Haal je bestelling op in een van onze restaurants
              </p>
              <DeliveryButton to="/takeaway">
                Bestel Nu <ArrowRight size={16} />
              </DeliveryButton>
            </DeliveryCard>

            <DeliveryCard
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="icon">📍</div>
              <h3>Vind een Restaurant</h3>
              <p>
                Bekijk al onze locaties en maak een reservering
              </p>
              <DeliveryButton to="/restaurants">
                Bekijk Locaties <ArrowRight size={16} />
              </DeliveryButton>
            </DeliveryCard>
          </DeliveryGrid>
        </DeliveryContent>
      </DeliverySection>
    </PageContainer>
  );
};

export default HomePage;
